"use client";

import { developerSteps } from "@/data/main-data";
import { motion, useScroll, useSpring } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { useRef } from "react";

export default function Process() {
  const locale = useLocale();
  const t = useTranslations("Home");
  const isRtl = locale === "ar";

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    <section
      ref={containerRef}
      id="process"
      className="relative py-24 md:py-40 overflow-hidden bg-[#06080D]"
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(200,169,110,0.05),transparent_70%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(200,169,110,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(200,169,110,0.02)_1px,transparent_1px)] bg-size-[60px_60px] mask-[radial-gradient(ellipse_at_center,black,transparent_80%)]" />
      </div>

      <div className="relative z-10 container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-24 md:mb-32"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-10 bg-linear-to-r from-transparent to-[#C8A96E]" />
            <span className="text-[#C8A96E] uppercase tracking-[0.4em] text-xs font-bold font-cairo">
              {t("work")}
            </span>
            <div className="h-px w-10 bg-linear-to-l from-transparent to-[#C8A96E]" />
          </div>

          <h2 className="text-4xl md:text-6xl font-black text-[#EDE5D6] leading-tight font-cairo">
            {t("journey")}
          </h2>

          <p className="text-[#EDE5D6]/40 mx-auto mt-6 max-w-2xl text-base md:text-lg font-light font-cairo">
            {t("steps")}
          </p>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute top-10 left-0 right-0 h-0.5 bg-white/5">
            <motion.div
              style={{ scaleX, originX: isRtl ? 1 : 0 }}
              className="h-full bg-linear-to-r from-[#C8A96E] via-[#F5E4B8] to-[#C8A96E] shadow-[0_0_15px_rgba(200,169,110,0.5)]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-8">
            {developerSteps.map((step, index) => {
              const IconComponent = step.icon;
              const delay = index * 0.2;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay }}
                  viewport={{ once: true }}
                  className="relative group flex flex-col items-center"
                >
                  <div className="relative z-20 mb-10">
                    <div className="w-20 h-20 rounded-2xl bg-[#11141D] border border-[#C8A96E]/20 flex items-center justify-center rotate-45 transition-all duration-500 group-hover:-rotate-135 group-hover:border-[#C8A96E] group-hover:shadow-[0_0_30px_rgba(200,169,110,0.2)]">
                      <div className="-rotate-45 group-hover:-rotate-135 transition-all duration-500">
                        {IconComponent && (
                          <IconComponent className="w-9 h-9 text-[#C8A96E]" />
                        )}
                      </div>
                    </div>
                    <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#C8A96E] text-[#06080D] font-black text-xs flex items-center justify-center shadow-lg">
                      {step.id}
                    </div>
                  </div>

                  <div className="text-center px-4">
                    <h3 className="text-xl md:text-2xl font-bold text-[#EDE5D6] mb-4 group-hover:text-[#C8A96E] transition-colors duration-300 font-cairo">
                      {isRtl ? step.title_ar : step.title_en}
                    </h3>

                    <div
                      className={`h-1 w-12 bg-[#C8A96E]/20 mx-auto mb-4 transition-all duration-500 group-hover:w-24 group-hover:bg-[#C8A96E]`}
                    />

                    <p className="text-[#EDE5D6]/40 text-sm leading-relaxed font-light font-cairo">
                      {isRtl ? step.description_ar : step.description_en}
                    </p>
                  </div>

                  {index < developerSteps.length - 1 && (
                    <div className="lg:hidden absolute left-1/2 top-full w-px h-16 bg-linear-to-b from-[#C8A96E]/50 to-transparent -translate-x-1/2" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-linear-to-t from-[#C8A96E]/5 to-transparent pointer-events-none" />
    </section>
  );
}
